import { useState } from "react";
import { toast } from "sonner";
import { Navigation, X, Copy, MapPin, MoreHorizontal } from "lucide-react";
import { Btn } from "../../admin/AdminUI";

const PREF_KEY = "vpw_nav_pref";

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent);
const isAndroid = () => /Android/i.test(navigator.userAgent);

const APPS = {
  google: { label: "Google Maps", url: (lat, lon) => `https://www.google.com/maps/dir/?api=1&destination=${lat},${lon}&travelmode=driving&dir_action=navigate` },
  apple: { label: "Apple Maps", url: (lat, lon) => `https://maps.apple.com/?daddr=${lat},${lon}&dirflg=d` },
  waze: { label: "Waze", url: (lat, lon) => `https://waze.com/ul?ll=${lat},${lon}&navigate=yes` },
};

const orderedKeys = () => {
  if (isIOS()) return ["apple", "google", "waze"];
  if (isAndroid()) return ["google", "waze", "apple"];
  return ["google", "apple", "waze"];
};

// Reusable navigation handoff. Reusable from Quotes, Jobs, Customers, Leads.
export default function NavigateToProperty({ address, latitude, longitude, verified, confirmed = true, compact = false }) {
  const [open, setOpen] = useState(false);
  const [remember, setRemember] = useState(false);
  const hasCoords = latitude != null && longitude != null;
  const hasDest = hasCoords || !!address;

  const launch = (key) => {
    const app = APPS[key];
    let url;
    if (hasCoords) url = app.url(latitude, longitude);
    else url = key === "apple"
      ? `https://maps.apple.com/?daddr=${encodeURIComponent(address)}&dirflg=d`
      : key === "waze"
        ? `https://waze.com/ul?q=${encodeURIComponent(address)}&navigate=yes`
        : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}&travelmode=driving`;
    if (remember) localStorage.setItem(PREF_KEY, key);
    window.open(url, "_blank", "noopener,noreferrer");
    setOpen(false);
  };

  const onNavigate = () => {
    if (!hasDest) { toast.error("Navigation unavailable — property address required."); return; }
    if (!confirmed) {
      if (!window.confirm("This property has not been confirmed. Navigate anyway?")) return;
    }
    const pref = localStorage.getItem(PREF_KEY);
    if (pref && APPS[pref]) { launch(pref); return; }
    setOpen(true);
  };

  const copyAddress = async () => { try { await navigator.clipboard.writeText(address || ""); toast.success("Address copied."); } catch { toast.error("Copy failed"); } setOpen(false); };
  const copyCoords = async () => { try { await navigator.clipboard.writeText(hasCoords ? `${latitude}, ${longitude}` : ""); toast.success("Coordinates copied."); } catch { toast.error("Copy failed"); } };

  return (
    <>
      <div className="inline-flex items-center gap-1">
        <Btn variant="blue" onClick={onNavigate} className={compact ? "!py-1.5 !px-3" : ""} data-testid="navigate-property-btn">
          <Navigation className="w-4 h-4" /> Navigate{compact ? "" : " to Property"}
        </Btn>
        {localStorage.getItem(PREF_KEY) && (
          <button onClick={() => setOpen(true)} title="Choose navigation app" className="p-1.5 text-gray-400 hover:text-[var(--navy)]" data-testid="navigate-more">
            <MoreHorizontal className="w-4 h-4" />
          </button>
        )}
      </div>
      {!verified && hasDest && <p className="text-[11px] text-amber-600 mt-0.5">⚠ Unverified destination</p>}

      {open && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center" data-testid="navigate-chooser">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="relative bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-sm p-5 space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="font-head font-bold text-[var(--navy)] uppercase flex items-center gap-2"><MapPin className="w-4 h-4" /> Navigate With</h3>
              <button onClick={() => setOpen(false)}><X className="w-5 h-5" /></button>
            </div>
            <p className="text-xs text-gray-500 truncate">{address}</p>
            <div className="grid gap-2 pt-1">
              {orderedKeys().map((k) => (
                <Btn key={k} variant="ghost" onClick={() => launch(k)} className="!justify-start" data-testid={`nav-${k}`}>
                  <Navigation className="w-4 h-4" /> {APPS[k].label}
                </Btn>
              ))}
              <Btn variant="ghost" onClick={copyAddress} className="!justify-start" data-testid="nav-copy-address"><Copy className="w-4 h-4" /> Copy Address</Btn>
              {hasCoords && <button onClick={copyCoords} className="text-xs text-gray-400 hover:text-[var(--navy)] text-left px-1" data-testid="nav-copy-coords">Copy Coordinates</button>}
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-600 pt-1">
              <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="w-4 h-4 accent-[var(--action)]" data-testid="nav-remember" />
              Remember my choice
            </label>
          </div>
        </div>
      )}
    </>
  );
}
